import * as React from "react";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { useImportInvoiceDataMutation } from "../../generated/graphql";
import { useSnackbar } from "notistack";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
  fontSize: "16px",
  padding: "10px 20px",
  borderRadius: "8px",
}));

const Container = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(2),
  width: "100%",
  maxWidth: "500px",
  margin: "auto",
  boxShadow: theme.shadows[3],
}));

const CenteredBox = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "50vh",
});

export default function InvoiceImport() {
  const [fileName, setFileName] = React.useState("");
  const [file, setFile] = React.useState(null);
  const [isFileSelected, setIsFileSelected] = React.useState(false);
  const [isDisabled, setIsDisabled] = React.useState(false);
  const [importFileData] = useImportInvoiceDataMutation();
  const { enqueueSnackbar } = useSnackbar();

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFileName(selectedFile.name);
      setFile(selectedFile);
      setIsFileSelected(true);
    }
  };

  const handleSubmit = async () => {
    if (!file) return;

    try {
      setIsDisabled(true);
      const response = await importFileData({ variables: { file } });
      if (response.data?.importInvoiceData) {
        enqueueSnackbar("File Imported Successfully", { variant: "success" });
      } else {
        enqueueSnackbar(
          "Error importing file: Campaign not found",
          { variant: "error" }
        );
      }
    } catch (error) {
      enqueueSnackbar("Error generating report");
      console.error("Error uploading file", error);
    } finally {
        setIsDisabled(false); 
      }
    setFileName("");
    setIsFileSelected(false);
    setFile(null);
    document.getElementById("file-input").value = null;
  };

  return (
    <div>
      <CenteredBox>
        <Container elevation={3}>
          <Typography variant="h5" component="h2" gutterBottom>
            Import Invoice No.
          </Typography>
          <Typography variant="body1" color="textSecondary" align="center">
            Upload a XLSX file to import costs data.
          </Typography>
          <StyledButton component="label" startIcon={<CloudUploadIcon />}>
            Upload File
            <VisuallyHiddenInput
              id="file-input"
              type="file"
              onChange={handleFileChange}
            />
          </StyledButton>
          {fileName && (
            <Typography variant="body2" color="textSecondary" align="center">
              Selected file: {fileName}
            </Typography>
          )}
          {isFileSelected && (
            <StyledButton
              variant="contained"
              color="secondary"
              onClick={handleSubmit}
              disabled={isDisabled}
              style={{ marginTop: "20px" }}
            >
              { isDisabled ? 'Submtting ...' : 'Submit' }
            </StyledButton>
          )}
        </Container>
      </CenteredBox>
    </div>
  );
}
